//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useDebounce } from 'use-debounce';

import {
  CategorySortCriteria,
  SamplingCategoriesResponse,
} from '@apis/campaign-requests/sampling/products/samplingCategoriesApiModel';
import { useGetSamplingCategoriesQuery } from '@campaigns/components/campaign-requests/sampling-campaigns/SamplingCampaignsQueries';
import { ProductDetailsFieldLabels } from '@campaigns/components/shared/product-details-modal/drawer/constants/ProductDetailsModalFieldLabels';
import { TypeaheadSearchField } from '@campaigns/components/shared/product-details-modal/drawer/form/TypeaheadSearchField';
import { PRODUCT_MODAL_FIELD_IDS } from '@campaigns/constants/ProductModalFieldIds';
import { SEARCH_DEBOUNCE_DELAY } from '@campaigns/constants/TimeConstants';
import { SAMPLING_CAMPAIGN_PRODUCT_MODAL_CATEGORY_SEARCH_PREFIX } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { useSamplingCampaign } from '@campaigns/hooks/useSamplingCampaign';
import { formatProductCatalogSearch } from '@campaigns/utils/formatProductCatalogSearch';
import { DEFAULT_EMPTY_VALUE } from '@constants/DefaultEmptyValues';

const MAX_CATEGORY_RESULTS = 10;

interface CategoryOption {
  externalID: string;
  name: string;
}

interface CategorySearchFieldProps {
  error?: FieldError;
  onSelectCategory: (categoryName: string) => void;
  value?: string;
}

const selectCategoryOptions = (
  data: SamplingCategoriesResponse,
): CategoryOption[] => {
  if (!data.categories) {
    return [];
  }
  return data.categories.map((category) => ({
    externalID: category.externalID ?? '',
    name: category.name ?? DEFAULT_EMPTY_VALUE,
  }));
};

export const CategorySearchField = ({
  error,
  onSelectCategory,
  value = '',
}: CategorySearchFieldProps) => {
  const { selectedRetailer } = useSamplingCampaign();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY);

  const formattedSearch = formatProductCatalogSearch(
    debouncedSearchTerm,
    'name',
  );

  const {
    data: categoryOptions = [],
    error: queryError,
    isPending,
  } = useGetSamplingCategoriesQuery<CategoryOption[]>(
    {
      active: true,
      limit: MAX_CATEGORY_RESULTS,
      retailerId: selectedRetailer?.clientId,
      searchText: formattedSearch,
      sortCriteria: CategorySortCriteria.Name,
    },
    selectCategoryOptions,
  );

  return (
    <TypeaheadSearchField
      debouncedSearchTerm={debouncedSearchTerm}
      error={error}
      fieldId={PRODUCT_MODAL_FIELD_IDS.CATEGORY}
      fieldLabelId={PRODUCT_MODAL_FIELD_IDS.CATEGORY_LABEL}
      helpText='Enter the category to which the product belongs.'
      isLoading={isPending}
      labelText={ProductDetailsFieldLabels.category}
      onSearch={setSearchTerm}
      onSelect={onSelectCategory}
      options={categoryOptions}
      queryError={queryError}
      resourceName='categories'
      searchPlaceholder='Search categories'
      trackingIdPrefix={SAMPLING_CAMPAIGN_PRODUCT_MODAL_CATEGORY_SEARCH_PREFIX}
      value={value}
      visuallyHiddenLabel='Search categories'
    />
  );
};
